import { Input, Modal } from 'antd';
import CloseIcon from 'assets/icons/CloseIcon';
import useThemes from 'hooks/useThemes';
import { BetVoiceResponseModel, PlacingBet } from 'models';
import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import { platformStates } from 'redux/platformSlice';
import Typo from 'consts/typography';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import { IMG_ALT, MARKET_SPREADS, PLACE_BET_AUDIO, WAGERS_PLAYER_PROPS } from 'consts/app';
import graph from 'assets/images/graph.png';
import { selectCurrentConverter } from 'features/home/homeSlice';
import BigNumber from 'bignumber.js';
import ConditionButton from 'components/Commons/ConditionButton';
import useUserDetail from 'hooks/useUserDetail';
import oddsApi from 'api/oddsApi';
import { showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { useHandleInsufficientBalance } from 'hooks/useHandleInsufficientBalance';
interface BetModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
  bet: BetVoiceResponseModel;
}

const getBetName = (bet: BetVoiceResponseModel | undefined): string => {
  if (!bet) return '';
  if (bet.odds.market === 'total_goals' || bet.odds.market === 'total_points') return bet.odds.name;
  if (MARKET_SPREADS.includes(bet.odds.market) || WAGERS_PLAYER_PROPS.includes(bet.odds.market))
    return bet.odds.name;
  return bet.odds.selection;
};
const getType = (bet: BetVoiceResponseModel | undefined): string => {
  if (!bet) return 'TOTAL POINT';

  if (WAGERS_PLAYER_PROPS.includes(bet.odds.market)) return bet.odds.marketName.toUpperCase();

  switch (bet.odds.market) {
    case '1st_half_point_spread':
    case '1st_half_run_line':
    case '1st_period_puck_line':
    case 'first_half_point_spread':
    case 'first_half_run_line':
    case 'first_period_puck_line':
    case 'run_line_after_5_innings':
      return 'First half spread'.toUpperCase();
    case '1st_half_moneyline':
    case '1st_period_moneyline':
    case 'first_half_money_line':
    case 'first_period_money_line':
    case 'first_5_innings_money_line':
      return 'First half money'.toUpperCase();
    case '1st_half_total_points':
    case '1st_half_total_runs':
    case '1st_period_total_goals':
    case 'first_half_total_points':
    case 'first_half_total_runs':
    case 'first_period_total_goals':
    case 'first_period_total_goals_over_under':
    case 'first_half_total_points_over_under':
    case 'total_runs_after_5_innings_over_under':
      return 'First half total'.toUpperCase();
    case 'point_spread':
    case 'run_line':
    case 'puck_line':
      return 'Spread'.toUpperCase();
    case 'moneyline':
    case 'money_line':
    case 'match_result':
    case 'match_winner':
    case 'fight_result_draw_no_bet_':
      return 'Moneyline'.toUpperCase();
    default:
      return 'Total point'.toUpperCase();
  }
};
const getPossibleWin = (amount: number, decimalPrice: number): string => {
  const amountBigNumber = new BigNumber(amount);
  return amountBigNumber.times(new BigNumber(decimalPrice)).minus(amountBigNumber).toFixed(2);
};
const aud = new Audio(PLACE_BET_AUDIO);

const BetModal: React.FunctionComponent<BetModalProps> = (props) => {
  const { title, visible, closeModal, className = '', bet } = props;
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const et = useErrTranslation();
  const platform = useAppSelector(platformStates);
  const { isDesktop, isLargeDesktop } = platform;
  const converter = useAppSelector(selectCurrentConverter);

  const { isUserLoggedIn } = useUserDetail();
  const { getWallet } = useHandleGetWallets();
  const handleResponseError = useHandleResponseError();
  const handleInsufficientBalance = useHandleInsufficientBalance();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [amount, setAmount] = useState(bet.amount ? bet.amount : 0);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]{1,10}$/;
    const value = event.target.value;
    if (value === '' || regex.test(value)) {
      setAmount(Number(value));
    }
  };

  const placeBet = async () => {
    setIsLoading(true);
    const placingBetRequest: PlacingBet[] = [
      {
        gameId: bet.games[0].id,
        oddsId: bet.odds.id,
        amount: amount,
        futureId: '',
        market: bet.odds.market,
      },
    ];

    const { ok, error } = await oddsApi.placingBets({
      bets: placingBetRequest,
    });
    setIsLoading(false);
    if (ok) {
      aud.play();
      getWallet();
      showSuccessModal({
        title: t('bet.success.title'),
        content: t('bet.success.message'),
      });
      closeModal();
    }
    if (error) {
      if (error.message === 'error.validate.balance.insufficient') {
        return handleInsufficientBalance();
      }
      if (error?.fieldErrors) {
        showErrorModal({
          content: et(error.fieldErrors.bets[0]),
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
          onOk: closeModal(),
        });
      } else handleResponseError(error);
    }
  };

  useEffect(() => {
    return () => {
      setAmount(0);
      setIsLoading(false);
    };
  }, []);

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={closeModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {bet.odds ? (
        <>
          <div className="bet-information__item">
            <div className="bet-information__item__header">
              <span
                className={` primary-text-color ${isDesktop || isLargeDesktop ? Typo.p1 : Typo.p3}`}
              >
                {`${bet.games[0].homeTeam} vs ${bet.games[0].awayTeam}`}
              </span>
            </div>
            <div className="bet-information__item__body mt-10">
              <div className="flex-col">
                <span
                  className={`primary-text-color ${
                    isDesktop || isLargeDesktop ? Typo.p1 : Typo.p3
                  }`}
                >
                  {getBetName(bet)}
                </span>
                {bet?.games[0]?.sport.group !== 'Soccer' && (
                  <span
                    className={`primary-text-color ${
                      isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7
                    }`}
                  >
                    {getType(bet)}
                  </span>
                )}
              </div>
            </div>
            <div className="bet-information__item__footer mt-10">
              <div className="image-bet">
                <img
                  src={graph}
                  alt={IMG_ALT}
                  className={currentTheme === 'dark' ? '' : 'light-img-bet'}
                />
              </div>
              <div className="number-bet">
                <span
                  className={`primary-text-color ${
                    isDesktop || isLargeDesktop ? Typo.p1 : Typo.p3
                  }`}
                >
                  {converter === 'decimal' ? (
                    <>
                      {bet?.odds.decimalPrice && (
                        <>
                          {`${bet.odds.decimalPrice > 0 ? '+' : ''}${new BigNumber(
                            bet.odds.decimalPrice,
                          ).toFixed(2)}`}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {bet.odds.price && (
                        <>
                          {`${bet.odds.price > 0 ? '+' : ''}${new BigNumber(bet.odds.price).toFixed(
                            0,
                          )}`}
                        </>
                      )}
                    </>
                  )}
                </span>
              </div>
            </div>
            <div className="bet-information__item__footer mt-10">
              <div className="number-bet">
                <span
                  className={`primary-text-color ${
                    isDesktop || isLargeDesktop ? Typo.p1 : Typo.p3
                  }`}
                >
                  {t('myBet.possibleWin')}: {getPossibleWin(amount, bet.odds.decimalPrice)} $
                </span>
              </div>
            </div>
          </div>
          <Input
            name="input-value"
            className={`mt-10 input-secondary ${isDesktop || isLargeDesktop ? Typo.p1 : Typo.p2}`}
            placeholder="Bet Amount"
            onChange={onChange}
            value={amount || 0}
          />
          <div className="button-pay">
            <ConditionButton
              onClick={placeBet}
              className={`mt-10 cursor-pointer button-${
                isLoading || amount === 0 ? 'disabled' : 'secondary'
              } ${platform.isDesktop || platform.isLargeDesktop ? Typo.p1 : Typo.p3}`}
              disabled={isLoading || amount === 0}
              content={
                isLoading ? (
                  <div className="spinner"></div>
                ) : (
                  `${t('bet.placeBet')} ${amount.toFixed(2)} $`
                )
              }
              testerCheck={true}
            />
          </div>
        </>
      ) : (
        <div>
          <EmptyTableIcon />
        </div>
      )}
    </Modal>
  );
};

export default BetModal;
